import React, { useState } from 'react';
import { FaLock, FaPaperPlane, FaTimes, FaShieldAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './ConsentRequestModal.css';
import API_BASE_URL from '../../config/api';

const stripEmailAlias = (email) => {
  if (!email) return '';
  return email.replace(/\+[^@]+@/, '@');
};

const ConsentRequestModal = ({ isOpen, targetUser, onClose, onRequestSent }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [note, setNote] = useState('');

  if (!isOpen || !targetUser) return null;

  const isPending = targetUser.consentStatus === 'Pending';
  const isRejected = targetUser.consentStatus === 'Rejected';

  const handleSendRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/family-consent/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          targetUserId: targetUser.id,
          note: note.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        if (onRequestSent) onRequestSent();
      } else {
        setError(data.message || 'Erro ao enviar o pedido de autorização.');
      }
    } catch (err) {
      console.error('Consent request error:', err);
      setError('Ocorreu um erro de ligação ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSuccess(false);
    setError(null);
    setNote('');
    onClose();
  };

  return (
    <div className="consent-modal-overlay" onClick={handleModalClose}>
      <div className="consent-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="consent-modal-close" onClick={handleModalClose} aria-label="Fechar">
          <FaTimes />
        </button>

        <div className="consent-modal-header">
          <div className="consent-icon-badge">
            <FaShieldAlt />
          </div>
          <h2>Autorização de Acesso Familiar (RGPD)</h2>
          <p className="consent-subtitle">
            O familiar <strong>{targetUser.firstName} {targetUser.lastName}</strong> é maior de idade (18+ anos).
          </p>
        </div>

        <div className="consent-modal-body">
          {success ? (
            <div className="consent-success-box">
              <FaCheckCircle className="success-icon" />
              <h3>Pedido de Autorização Enviado!</h3>
              <p>
                Enviamos uma notificação por email para <strong>{stripEmailAlias(targetUser.email)}</strong>.
                Assim que <strong>{targetUser.firstName}</strong> aprovar o pedido no Portal, o acesso à conta ficará disponível.
              </p>
              <button className="btn-primary" onClick={handleModalClose}>
                Entendido
              </button>
            </div>
          ) : (
            <>
              <div className="consent-info-box">
                <FaLock className="info-icon" />
                <p>
                  Em conformidade com a legislação de Proteção de Dados (RGPD), o acesso a contas e dados pessoais de familiares maiores de idade necessita de <strong>autorização prévia e explícita</strong> concedida pelo próprio titular.
                </p>
              </div>

              {isPending && (
                <div className="consent-status-banner pending">
                  <FaExclamationTriangle />
                  <span>Já existe um pedido de autorização pendente enviado a este familiar.</span>
                </div>
              )}

              {isRejected && (
                <div className="consent-status-banner rejected">
                  <FaExclamationTriangle />
                  <span>O pedido anterior foi recusado pelo titular. Pode enviar um novo pedido.</span>
                </div>
              )}

              <form onSubmit={handleSendRequest}>
                <div className="form-group">
                  <label htmlFor="consent-note">Mensagem/Nota para o familiar (opcional):</label>
                  <textarea
                    id="consent-note"
                    rows="3"
                    placeholder="Ex: Olá! Solicito acesso à tua conta para consultar e regularizar quotas do clube."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={250}
                  />
                </div>

                {error && <div className="consent-error-message">{error}</div>}

                <div className="consent-modal-actions">
                  <button type="button" className="btn-secondary" onClick={handleModalClose} disabled={loading}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? (
                      'A enviar...'
                    ) : (
                      <>
                        <FaPaperPlane /> {isPending ? 'Reenviar Pedido' : 'Enviar Pedido de Autorização'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentRequestModal;
